import React, { useMemo, useCallback, useState, useEffect } from 'react'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ButtonActionType } from './index.type'
import type { ButtonItem } from './index.type'
import { useUserInfo, useCardListStatus } from '@/hooks'
import { toFixedNoRound, formatThousands, formatDateTime } from '@/utils'
import RefreshList from '@/components/RefreshList'
import { APIWeb } from '@/api'
import EventIcon from '@/components/EventIcon'
import LogoTitle from '@/components/logoTitle'
import Ripple from '@/components/Ripple'
import EmptyState from '@/components/EmptyState'
import SkeletonBox from '@/components/SkeletonBox'
import BindEmailDialog from '@/components/BindEmailDialog'
// import UpgradeNotice from '@/components/UpgradeNotice'
import { useAllRecordListType } from '@/hooks/listStatus'
import dayjs from 'dayjs'

export default React.memo(function Home() {
  const { t } = useTranslation()
  const { userInfo, loading: userInfoLoading, actions: userActions } = useUserInfo()
  const { getAllRecordListType } = useAllRecordListType()
  const navigate = useNavigate()
  // 资产流水列表相关状态
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true) // 首次加载标识
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // 绑定邮箱弹窗状态
  const [showBindEmailDialog, setShowBindEmailDialog] = useState(false)

  // 页面加载时刷新用户信息
  useEffect(() => {
    userActions.refreshUserInfo()
  }, [])

  // 检查用户是否绑定邮箱
  useEffect(() => {
    if (!userInfoLoading && userInfo) {
      // 如果用户信息已加载且没有邮箱，显示绑定邮箱弹窗
      if (!userInfo.email) {
        setShowBindEmailDialog(true)
      }
    }
  }, [userInfo, userInfoLoading])

  // const cardStates 

  // 通用的数据加载函数
  const fetchAssetFlowList = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading) return

    setLoading(true)
    try {
      const response = await APIWeb.getAllRecord({
        page,
        page_size: pageSize
      })
      if (response?.list) {
        const newData = response.list

        if (isAppend) {
          // 追加数据（用于加载更多）
          setData(prevData => [...prevData, ...newData])
        } else {
          // 替换数据（用于初始加载）
          setData(newData)
        }

        // 检查是否还有更多数据
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setHasMore(true)
          if (!isAppend) {
            setCurrentPage(2) // 初始加载后，下次从第2页开始
          } else {
            setCurrentPage(prev => prev + 1) // 加载更多时，页码递增
          }
        }
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error('获取卡片记录失败:', error)
      setHasMore(false)
    } finally {
      setLoading(false)
      setIsInitialLoading(false) // 首次加载完成后设置为 false
    }
  }, [pageSize])

  // 加载更多数据
  const loadMore = useCallback(async () => {
    await fetchAssetFlowList(currentPage, true)
  }, [fetchAssetFlowList, currentPage])

  // 下拉刷新处理函数（仅刷新交易记录列表）
  const onRefresh = useCallback(async () => {
    // 直接重新加载第一页数据，fetchAssetFlowList 会自动处理状态
    await fetchAssetFlowList(1, false)
  }, [fetchAssetFlowList])

  const navigateToTransactionHistory = useCallback((item: any) => {
    if (item.noLine) return
    navigate('/account/transaction-history', {
      state: {
        coinName: item.name
      }
    })
  }, [navigate])

  // 处理卡片记录点击
  // const handleRecordClick = useCallback((record: CardRecordItem, avatarSvg: string) => {
  //   navigate('/card-v2/consumption-detail', {
  //     state: {
  //       record: {
  //         ...record,
  //         avatarSvg
  //       }
  //     }
  //   })
  // }, [navigate])

  const buttonItems = useMemo<ButtonItem[]>(
    () => ([
      {
        actionType: ButtonActionType.Deposit,
        icon: '/icon/assets-icon1.svg',
        text: t('存款')
      },
      {
        actionType: ButtonActionType.Remittance,
        icon: '/icon/assets-icon2.svg',
        text: t('汇款')
      },
      {
        actionType: ButtonActionType.Exchange,
        icon: '/icon/assets-icon3.svg',
        text: t('兑换')
      },
      {
        actionType: ButtonActionType.Transfer,
        icon: '/icon/assets-icon5.svg',
        text: t('转账')
      }
    ]),
    [t]
  )

  const cardItems = useMemo(
    () => ([
      { icon: '/images/home/usdt.png', name: 'USDT', text2: formatThousands(userInfo?.usdt, 2, true), text3: `≈ ${formatThousands(userInfo?.usdt, 2, true)} USD` },
      { icon: '/images/home/usdc.png', name: 'USDC', text2: formatThousands(userInfo?.usdc, 2, true), text3: `≈ ${formatThousands(userInfo?.usdc, 2, true)} USD` },
      { icon: '/images/home/usd1.png', name: 'USD1', text2: formatThousands(userInfo?.usd1, 2, true), text3: `≈ ${formatThousands(userInfo?.usd1, 2, true)} USD` },
      { icon: '/images/home/usd.png', name: 'USD', text2: formatThousands(userInfo?.usd, 2, true), text3: `≈ ${formatThousands(userInfo?.usd, 2, true)} USD` },
    ]),
    [userInfo]
  )
  const { getCardListStatus } = useCardListStatus()

  // 处理KYC按钮点击事件 - 跳转到持卡人信息页面
  const handleKycButtonClick = useCallback(() => {
    navigate('/my/cardholder/add')
  }, [navigate])

  const handleButtonPress = useCallback(
    (actionType: ButtonActionType) => {
      switch (actionType) {
        case ButtonActionType.Deposit:
          navigate('/recharge/select-currency')
          break
        case ButtonActionType.Remittance:
          navigate('/remittance')
          break
        case ButtonActionType.Exchange:
          navigate('/exchange')
          break
        case ButtonActionType.Transfer:
          navigate('/transfer')
          break
        default:
          break
      }
    },
    [navigate]
  )

  // 处理绑定邮箱弹窗确认
  const handleBindEmailConfirm = useCallback(() => {
    setShowBindEmailDialog(false)
    navigate('/safety/bind-email')
  }, [navigate])

  const CardItems = useMemo(
    () => cardItems.map((item, index) => (
      <Ripple key={index} className="rounded-2xl bg-[#F5F5F5]">
        <div className="bg-[#F5F5F5] p-3.5 min-w-31 min-h-24" onClick={() => navigateToTransactionHistory(item)}>
          <div className="flex items-center mb-2">
            <div className="w-6 h-6 rounded-full overflow-hidden">
              <img className="w-6 h-6" src={item.icon} />
            </div>
            <span className="text-xs font-medium ml-2 text-gray-800">{item.name}</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{item.text2}</div>
          <div className="text-xs text-gray-500">{item.text3}</div>
        </div>
      </Ripple>
    )),
    [cardItems]
  )

  const renderRecordItem = useCallback((record: any, index: number) => {
    const changeNum = parseFloat(record.change) || 0
    const isPositive = changeNum >= 0
    const currencyName = record.type?.toUpperCase() || 'USD'
    const isFiat = ['USD'].includes(currencyName)

    return (
      <Ripple className='rounded-2'>
        <div className="flex items-center justify-between py-3 px-3.5" onClick={() => navigate('/record-detail', { state: { record } })}>
          <EventIcon event={record.event} className="w-8 h-8 mr-2" iconClassName="w-4 h-4" />
          <div className='flex-1'>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-900">
                {getAllRecordListType(record?.event, record?.event_string)}
              </div>
              <div className="font-semibold text-xs text-black">
                {isPositive ? '+' : ''}{isFiat ? '$' : ''}{formatThousands(changeNum, 2, true)}{!isFiat ? ` ${currencyName}` : ''}
              </div>
            </div>

            <div className='flex items-center justify-between text-right'>
              <div className="text-2.5 text-[#979797] mt-2">
                {record.created_at ? formatDateTime(record.created_at) : '-'}
              </div>
              <div className="font-normal text-xs text-[#808080] mt-2 break-all">
                {t('成功')}
              </div>
            </div>
          </div>
        </div>
      </Ripple>
    )
  }, [getCardListStatus, t])

  // 记录列表骨架屏 - 保持与 renderRecordItem 完全一致的布局
  const renderRecordSkeleton = useCallback(() => {
    return (
      <div className="flex items-center justify-between py-3 px-3.5">
        {/* 左侧头像骨架 */}
        <SkeletonBox className="flex w-8 h-8 bg-[#EAEEF6] rounded-full justify-center items-center mr-2 overflow-hidden flex-shrink-0" />

        {/* 右侧内容骨架 */}
        <div className='flex-1'>
          {/* 第一行：商户名称 + 金额 */}
          <div className="flex items-center justify-between mb-2">
            <SkeletonBox className="w-24 h-3 rounded" />
            <SkeletonBox className="w-16 h-3 rounded" />
          </div>

          {/* 第二行：时间 + 状态 */}
          <div className='flex items-center justify-between'>
            <SkeletonBox className="w-20 h-2.5 rounded" />
            <SkeletonBox className="w-12 h-2.5 rounded" />
          </div>
        </div>
      </div>
    )
  }, [])

  // Kyc提示 - kyc_status为false时显示
  const KycCertification = useMemo(
    () => (
      <div className="bg-white rounded-4 border-[0.5px] border-solid border-[#556AFF] mx-6 my-6 px-4 py-4 mb-0">
        <div className="flex items-center">
          <div className='w-4 h-4 bg-[#556AFF] rounded-full text-2 mr-4 text-white flex justify-center items-center'>i</div>
          <div className="flex-1">
            <div className="text-3.25 font-bold text-black mb-1">
              {t('添加持卡人')}
            </div>
            <div className="text-3.25 text-gray-600 mb-1.5">
              {t('完成持卡人身份验证后即可解锁完整服务')}
            </div>
            <button
              onClick={handleKycButtonClick}
              className="flex items-center text-[#556AFF] text-3.25 font-medium"
            >
              {t('前往添加')}
              <img src="/icon/go-right-icon.svg" alt="Arrow" className="ml-1" />
            </button>
          </div>
        </div>
      </div>
    ),
    [handleKycButtonClick, t]
  )

  const ButtonItems = useMemo(
    () => buttonItems.map((item, index) => (
      <div
        key={index}
        className="flex flex-col items-center justify-center bg-white rounded-lg"
        onClick={() => handleButtonPress(item.actionType)}
      >
        <Ripple className='rounded-full'>
          <div className='w-13.5 h-13.5 flex items-center justify-center rounded-full bg-[#556AFF]'>
            <img src={item.icon} alt={item.text} />
          </div>
        </Ripple>
        <div className="text-3 text-[#2C2C2C] min-h-4 mt-2">{item.text}</div>
      </div>
    )),
    [buttonItems, handleButtonPress]
  )

  return (
    <PageLayout tabbarPage={true}>
      {/* Header */}
      <Header
        leftChildren={
          <Ripple className='rounded-full'>
            <div className='w-10 h-10 bg-[#E4E4E4]/50 flex items-center justify-center rounded-full' onClick={() => navigate('/my')}>
              <img src="/icon/user-icon.svg" alt="User Icon" />
            </div>
          </Ripple>
        }
        centerChildren={<LogoTitle />}
        rightChildren={
          <Ripple className='rounded-full'>
            <div className='w-10 h-10 bg-[#556AFF] flex items-center justify-center rounded-full' onClick={() => navigate('/message')}>
              <img src="/icon/message-icon.svg" alt="Logo" />
            </div>
          </Ripple>
        }
      />

      {!userInfo?.kyc_status && KycCertification}

      {/* Assets */}
      <div className="bg-white  px-5 pt-6">
        {
          userInfoLoading ? <>
            <SkeletonBox className="w-20 h-6 rounded-2xl mb-1" />
            <SkeletonBox className="w-40 h-10 rounded-2xl my-1" />
          </> : <>
            <div className="text-base text-[#1A1A1A]/50 mb-1">{t('我的资产USD')}</div>
            <div className="text-8.75 font-bold text-black my-1">
              {formatThousands(userInfo?.balance || 0, 2, true)}
            </div>
          </>
        }

      </div>

      {/* Action Buttons */}
      <div className="flex justify-between my-5  px-5 ">
        {userInfoLoading ? <>
          {ButtonItems.map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center justify-center"
            >
              <SkeletonBox className="w-13.5 h-13.5 rounded-full mb-2"></SkeletonBox>
              <SkeletonBox className="w-10 min-h-4 rounded-2xl"></SkeletonBox>
            </div>
          ))}
        </> : <>{ButtonItems}</>}
      </div>

      {/* Currency Cards */}
      <div className="overflow-x-auto scrollbar-hide">
        <div className="inline-flex pb-2 px-5 space-x-3.5">
          {userInfoLoading ? <>
            {cardItems.map((item, index) => <SkeletonBox key={index} className="bg-[#F5F5F5] p-3.5 min-w-31 min-h-24 rounded-2xl" />)}
          </> : <>{CardItems}</>}

        </div>

      </div>
      {/* Records */}
      <div className='my-6 mx-5'>
        <div className="flex items-center justify-between mb-4">
          {isInitialLoading ? <>
            <SkeletonBox className="bg-[#F5F5F5] h-5 w-12 rounded-2xl"></SkeletonBox>
            <SkeletonBox className="bg-[#F5F5F5] h-5 w-6 rounded-2xl mr-1"></SkeletonBox>
          </> : <>
            <span className="text-3.5 text-[#1A1A1A]">{t('记录')}</span>
            <span className="text-3.5 text-[#1A1A1A]/65 mr-1" onClick={() => navigate('/all-records')}>. . .</span>
          </>}

        </div>
        <div className="bg-white py-3 border-[0.5px] border-solid border-[0.5px] border-[#E5E5E5]  rounded-3xl">
          <div className="max-h-100 min-h-70 flex flex-col" style={{ touchAction: 'pan-y' }}>
            <RefreshList
              data={data}
              renderItem={renderRecordItem}
              keyExtractor={(item, index) => `${item.id}-${index}`}
              loading={loading}
              hasMore={hasMore}
              onRefresh={onRefresh}
              onLoadMore={loadMore}
              renderSkeleton={renderRecordSkeleton}
              skeletonCount={5}
              emptyComponent={
                <EmptyState
                  text={t('暂无记录')}
                />
              }
              finishedText={t('没有更多数据了')}
              loadingText={t('加载中')}
              className='flex-1 overflow-y-auto scrollbar-hide'
            />
          </div>
        </div>
      </div>

      {/* 绑定邮箱弹窗 */}
      <BindEmailDialog
        visible={showBindEmailDialog}
        onConfirm={handleBindEmailConfirm}
      />

      {/* 临时通知 - 后续删除，删除时移除 UpgradeNotice 组件及其 import */}
      {/* <UpgradeNotice /> */}

    </PageLayout>
  )
})